import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSessao } from '@/features/authentication/sessao';
import { estiloPadrao } from '@/map/layers';
import { listarCamadas } from '@/services/geo-engine/requests';
import { useChamada } from '@/services/geo-engine/useChamada';
const SELECAO_PADRAO = { todos: true, ids: [] };
const RELEVO_PADRAO = {
    hillshadeAtivo: true,
    hillshadeIntensidade: 0.65,
};
const ContextoDeCamadas = createContext(null);
export function CamadasProvider({ children }) {
    const { token } = useSessao();
    const consulta = useChamada();
    // A identidade de `executar` muda a cada render, então nunca a usamos em deps de efeito.
    const executarRef = useRef(consulta.executar);
    executarRef.current = consulta.executar;
    const [catalogo, setCatalogo] = useState(null);
    const [ativas, setAtivas] = useState([]);
    const [carga, setCarga] = useState({});
    const [selecionadaId, setSelecionadaId] = useState(null);
    const [operacao, setOperacao] = useState(null);
    const [enquadramento, setEnquadramento] = useState(null);
    const [estilos, setEstilos] = useState({});
    const [rotulosPorCamada, setRotulosPorCamada] = useState({});
    const [relevo, setRelevo] = useState(RELEVO_PADRAO);
    const [selecaoPorCamada, setSelecaoPorCamada] = useState({});
    useEffect(() => {
        if (token === null) {
            return;
        }
        let cancelado = false;
        void executarRef.current((sinal) => listarCamadas(sinal)).then((resposta) => {
            if (cancelado || resposta === null) {
                return;
            }
            setCatalogo(resposta);
            const conhecidas = new Set(resposta.map((camada) => camada.id));
            setAtivas((atuais) => atuais.filter((id) => conhecidas.has(id)));
            setSelecionadaId((atual) => (atual !== null && conhecidas.has(atual) ? atual : null));
        });
        return () => {
            cancelado = true;
        };
    }, [token]);
    const recarregarCatalogo = useCallback(() => {
        let cancelado = false;
        void executarRef.current((sinal) => listarCamadas(sinal)).then((resposta) => {
            if (cancelado || resposta === null) {
                return;
            }
            setCatalogo(resposta);
        });
        return () => {
            cancelado = true;
        };
    }, []);
    const moverCamada = useCallback((camadaId, direcao) => {
        setAtivas((atuais) => {
            const posicao = atuais.indexOf(camadaId);
            const destino = direcao === 'subir' ? posicao + 1 : posicao - 1;
            if (posicao < 0 || destino < 0 || destino >= atuais.length) {
                return atuais;
            }
            const proximas = [...atuais];
            const deslocada = proximas[posicao];
            proximas[posicao] = proximas[destino];
            proximas[destino] = deslocada;
            return proximas;
        });
    }, []);
    const alternarAtiva = useCallback((id) => {
        setAtivas((atuais) => {
            if (atuais.includes(id)) {
                return atuais.filter((outro) => outro !== id);
            }
            return [...atuais, id];
        });
        setCarga((atual) => {
            if (!(id in atual)) {
                return atual;
            }
            const proximos = { ...atual };
            delete proximos[id];
            return proximos;
        });
    }, []);
    const definirCarga = useCallback((id, nova) => {
        setCarga((atual) => {
            const anterior = atual[id];
            if (anterior?.estado === nova.estado && anterior.mensagem === nova.mensagem) {
                return atual;
            }
            return { ...atual, [id]: nova };
        });
    }, []);
    const selecionar = useCallback((id) => setSelecionadaId(id), []);
    const definirOperacao = useCallback((colecao) => setOperacao(colecao), []);
    const enquadrar = useCallback((limites) => {
        setEnquadramento((atual) => ({ limites, sequencia: (atual?.sequencia ?? 0) + 1 }));
    }, []);
    // Estilos: `obterEstilo` lê do estado; sua identidade muda com `estilos`, mas quem o consome
    // sempre lê por id, então a mudança é observada pelos consumidores via `estilos` direto.
    const obterEstilo = useCallback((id, familia) => estilos[id] ?? estiloPadrao(familia), [estilos]);
    const definirEstilo = useCallback((id, estilo) => {
        setEstilos((atuais) => ({ ...atuais, [id]: estilo }));
    }, []);
    const restaurarEstiloPadrao = useCallback((id) => {
        setEstilos((atuais) => {
            if (!(id in atuais)) {
                return atuais;
            }
            const proximos = { ...atuais };
            delete proximos[id];
            return proximos;
        });
    }, []);
    // Rótulos: preferência independente do estilo geométrico e de `ativas`; padrão = ativos.
    const obterRotulosAtivos = useCallback((camadaId) => rotulosPorCamada[camadaId] ?? true, [rotulosPorCamada]);
    const definirRotulosAtivos = useCallback((camadaId, ativo) => {
        setRotulosPorCamada((atuais) => ({ ...atuais, [camadaId]: ativo }));
    }, []);
    const definirRelevo = useCallback((patch) => {
        setRelevo((atual) => ({ ...atual, ...patch }));
    }, []);
    const obterSelecaoDaCamada = useCallback((id) => selecaoPorCamada[id] ?? SELECAO_PADRAO, [selecaoPorCamada]);
    const definirSelecaoDaCamada = useCallback((id, selecao) => {
        setSelecaoPorCamada((atuais) => ({ ...atuais, [id]: selecao }));
    }, []);
    // `valor` do contexto: identidade estável por design — qualquer dependência é a referência
    // de estado primitivo ou um useCallback estável acima. Sem `executar` nem `consulta` aqui.
    const valor = useMemo(() => ({
        catalogo,
        carregandoCatalogo: consulta.carregando,
        erroDoCatalogo: consulta.erro,
        recarregarCatalogo,
        ativas,
        alternarAtiva,
        moverCamada,
        carga,
        definirCarga,
        selecionadaId,
        selecionar,
        operacao,
        definirOperacao,
        enquadramento,
        enquadrar,
        estilos,
        obterEstilo,
        definirEstilo,
        restaurarEstiloPadrao,
        rotulosPorCamada,
        obterRotulosAtivos,
        definirRotulosAtivos,
        relevo,
        definirRelevo,
        selecaoPorCamada,
        obterSelecaoDaCamada,
        definirSelecaoDaCamada,
    }), [
        catalogo,
        consulta.carregando,
        consulta.erro,
        recarregarCatalogo,
        ativas,
        alternarAtiva,
        moverCamada,
        carga,
        definirCarga,
        selecionadaId,
        selecionar,
        operacao,
        definirOperacao,
        enquadramento,
        enquadrar,
        estilos,
        obterEstilo,
        definirEstilo,
        restaurarEstiloPadrao,
        rotulosPorCamada,
        obterRotulosAtivos,
        definirRotulosAtivos,
        relevo,
        definirRelevo,
        selecaoPorCamada,
        obterSelecaoDaCamada,
        definirSelecaoDaCamada,
    ]);
    return _jsx(ContextoDeCamadas.Provider, { value: valor, children: children });
}
export function useCamadas() {
    const camadas = useContext(ContextoDeCamadas);
    if (camadas === null) {
        throw new Error('useCamadas exige CamadasProvider acima na árvore');
    }
    return camadas;
}
